import translate from '@translate';
import React, { FC, useEffect, useState } from 'react';
import { OrderType } from 'src/types/order';
import Typography from 'src/components/Typography';
import {
    Accordion,
    AccordionDetails,
    AccordionDetailsProps,
    AccordionProps,
    AccordionSummary,
    AccordionSummaryProps,
} from '@mui/material';
import { styled } from '@mui/system';
import { OrderTransportHistoryType } from 'src/types/orderDetail';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { renderTrackingNumber } from './functions';
import Steps from 'src/components/Steps';
import { DateType } from 'src/types/date';
import moment from 'moment';
import TextField, { TextFieldValueType } from 'src/components/TextField';
import { colors, sizes } from 'src/themes/default';

export const StyledAccordion = styled(Accordion)<AccordionProps>(({ theme }) => ({}));
export const StyledAccordionSummary = styled(AccordionSummary)<AccordionSummaryProps>(({ theme }) => ({
    minHeight: '34px',

    '& a': {
        color: colors.text_T0,
        margin: '-1px 10px',
        height: '20px',
        overflow: 'hidden',
    },
}));
export const StyledAccordionDetails = styled(AccordionDetails)<AccordionDetailsProps>(({ theme }) => ({
    '& svg': {
        color: colors.primary_O,
        width: '24px',
        height: '24px',
    },

    '& .MuiStepConnector-root.MuiStepConnector-vertical': {
        marginLeft: '10px',
    },
}));

export const formatDate = (date: DateType) => {
    if (!date) {
        return date;
    }
    return moment(date).format('LLL');
};

export interface TransportHistoryPropsType {
    orderData: OrderType | null;
    data: OrderTransportHistoryType[] | null;
}

const TransportHistory: FC<TransportHistoryPropsType> = (props) => {
    const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});
    const [formatedData, setFormatedData] = useState<OrderTransportHistoryType[]>([]);
    const [filteredData, setFilteredData] = useState<OrderTransportHistoryType[]>([]);
    const [filterTrackingNumber, setFilterTrackingNumber] = useState<TextFieldValueType>('');

    useEffect(() => {
        if (props.data) {
            const newformatedData = props.data.map((tracking) => ({
                ...tracking,
                history:
                    tracking.history.length < 2
                        ? tracking.history
                        : tracking.history
                              .map((historyItem) => ({ ...historyItem }))
                              .sort((a, b) => {
                                  return a.date && b.date ? moment(a.date).diff(moment(b.date)) : -1;
                              })
                              .reverse()
                              .map((historyItem) => ({
                                  ...historyItem,
                                  date: formatDate(historyItem.date),
                              })),
            }));
            setFormatedData(newformatedData);
            if (newformatedData.length) {
                const newExpadned: { [key: string]: boolean } = {};
                newExpadned[newformatedData[0].trackingNumber] = true;
                setExpanded(newExpadned);
            }
        }
    }, [props.data]);

    useEffect(() => {
        if (filterTrackingNumber) {
            setFilteredData(
                formatedData.filter(
                    (tracking) =>
                        tracking.trackingNumber.toLowerCase().indexOf(String(filterTrackingNumber).toLowerCase()) > -1
                )
            );
        } else {
            setFilteredData(formatedData);
        }
    }, [formatedData, filterTrackingNumber]);

    const handleToggleExpand = (trackingNumber: string) => {
        const newExpanded = { ...expanded };
        if (newExpanded[trackingNumber]) {
            delete newExpanded[trackingNumber];
        } else {
            newExpanded[trackingNumber] = true;
        }
        setExpanded(newExpanded);
    };

    if (!props.data) {
        return (
            <Typography variant="h2" align="center" sx={{ marginTop: sizes.blocks_top_MG }}>
                {translate('orderDetail.emptyData')}
            </Typography>
        );
    }

    const onRedirect = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.stopPropagation();
    };

    return (
        <div>
            {props.data?.length > 1 && (
                <TextField
                    label={translate('orderDetail.filterTrackingNumber')}
                    value={filterTrackingNumber}
                    onChange={(value) => setFilterTrackingNumber(value)}
                    name="filterTrackingNumber"
                />
            )}
            {filteredData.map((tracking) => (
                <StyledAccordion
                    key={tracking.trackingNumber}
                    expanded={!!expanded[tracking.trackingNumber]}
                    onChange={() => handleToggleExpand(tracking.trackingNumber)}
                >
                    <StyledAccordionSummary
                        aria-controls={tracking.trackingNumber}
                        id={tracking.trackingNumber}
                        expandIcon={<ExpandMoreIcon />}
                    >
                        <Typography variant="h3">{tracking.trackingNumber}</Typography>
                        {props.orderData &&
                            renderTrackingNumber(
                                {
                                    SHIPPER: props.orderData.SHIPPER,
                                    STATUS: props.orderData.STATUS,
                                    ORDER_TYPE: 'b2c',
                                    TRACKING_NUMBER: tracking.trackingNumber,
                                },
                                'TRACKING_NUMBER',
                                <OpenInBrowserIcon />,
                                onRedirect
                            )}
                    </StyledAccordionSummary>
                    <StyledAccordionDetails>
                        {!!expanded[tracking.trackingNumber] && (
                            <Steps
                                steps={tracking.history.map((historyItem, historyKey) => {
                                    return {
                                        label: (
                                            <div key={String(historyKey)}>
                                                <Typography variant="body2">{historyItem.text.replace(/&#(\d+);/g, (_, code) => String.fromCharCode(code))}</Typography>
                                                {historyItem.date && (
                                                    <Typography variant="caption">{historyItem.date}</Typography>
                                                )}
                                            </div>
                                        ),
                                        icon: <RadioButtonCheckedIcon fontSize="small" />,
                                    };
                                })}
                            />
                        )}
                    </StyledAccordionDetails>
                </StyledAccordion>
            ))}
        </div>
    );
};

export default TransportHistory;
